/**
 * @fileoverview Google Integration Configuration Component
 * Allows users to connect Google services (Calendar, Sheets),
 * manage OAuth scopes, and configure sync settings.
 */

import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  Loader2,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  Trash2,
  Calendar,
  FileSpreadsheet,
  Settings2,
  Mail,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

// Google OAuth configuration
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const GOOGLE_REDIRECT_URI = `${window.location.origin}/settings/integrations/google/callback`;

// Available scopes with descriptions
const AVAILABLE_SCOPES = [
  {
    id: 'calendar',
    name: 'Google Calendar',
    description: 'Create callback events and sync appointments',
    scopes: [
      'https://www.googleapis.com/auth/calendar.events',
      'https://www.googleapis.com/auth/calendar.readonly',
    ],
    icon: Calendar,
    iconBgClass: 'bg-blue-100 dark:bg-blue-900',
    iconColorClass: 'text-blue-600 dark:text-blue-400',
  },
  {
    id: 'sheets',
    name: 'Google Sheets',
    description: 'Export call logs to spreadsheets',
    scopes: [
      'https://www.googleapis.com/auth/spreadsheets',
      'https://www.googleapis.com/auth/drive.readonly',
    ],
    icon: FileSpreadsheet,
    iconBgClass: 'bg-green-100 dark:bg-green-900',
    iconColorClass: 'text-green-600 dark:text-green-400',
  },
];

interface GoogleIntegration {
  id: string;
  status: 'active' | 'disconnected' | 'error';
  config: {
    email?: string;
    name?: string;
    picture?: string;
    scopes?: string[];
    calendar_enabled?: boolean;
    sheets_enabled?: boolean;
    auto_create_events?: boolean;
    export_spreadsheet_id?: string;
  };
  last_error?: string;
  last_sync_at?: string;
  token_expires_at?: string;
  created_at: string;
}

export function GoogleConfig() {
  const { activeTenantId } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [selectedScopes, setSelectedScopes] = useState<string[]>(['calendar']);
  const [autoCreateEvents, setAutoCreateEvents] = useState(true);

  // Fetch current Google integration status
  const { data: integration, isLoading: isLoadingIntegration } = useQuery({
    queryKey: ['google-integration', activeTenantId],
    queryFn: async () => {
      if (!activeTenantId) return null;

      const { data, error } = await supabase
        .from('calie_integrations')
        .select('*')
        .eq('tenant_id', activeTenantId)
        .eq('provider', 'google')
        .maybeSingle();

      if (error) throw error;
      return data as GoogleIntegration | null;
    },
    enabled: !!activeTenantId,
  });

  // Initialize settings from integration config
  useEffect(() => {
    if (integration?.config) {
      setAutoCreateEvents(integration.config.auto_create_events ?? true);
    }
  }, [integration]);

  // Save settings
  const saveSettingsMutation = useMutation({
    mutationFn: async () => {
      if (!activeTenantId || !integration) throw new Error('No integration');

      const { error } = await supabase
        .from('calie_integrations')
        .update({
          config: {
            ...integration.config,
            auto_create_events: autoCreateEvents,
          },
          updated_at: new Date().toISOString(),
        })
        .eq('id', integration.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-integration', activeTenantId] });
      toast({
        title: 'Settings saved',
        description: 'Your Google integration settings have been updated.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Error saving settings',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Disconnect Google
  const disconnectMutation = useMutation({
    mutationFn: async () => {
      if (!activeTenantId) throw new Error('No tenant');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('Calie_google/disconnect', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { tenantId: activeTenantId },
      });

      if (response.error) throw response.error;
      if (!response.data?.success) throw new Error(response.data?.error || 'Disconnect failed');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-integration', activeTenantId] });
      toast({
        title: 'Google disconnected',
        description: 'Your Google integration has been removed.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Error disconnecting',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Refresh token
  const refreshTokenMutation = useMutation({
    mutationFn: async () => {
      if (!activeTenantId) throw new Error('No tenant');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('Calie_google/refresh', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { tenantId: activeTenantId },
      });

      if (response.error) throw response.error;
      if (!response.data?.success) throw new Error(response.data?.error || 'Refresh failed');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-integration', activeTenantId] });
      toast({
        title: 'Token refreshed',
        description: 'Your Google connection has been refreshed.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Refresh failed',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Handle Google OAuth
  const handleConnect = () => {
    // Build scopes from selected services
    const scopes = [
      'openid',
      'email',
      'profile',
      ...selectedScopes.flatMap(scopeId => {
        const scopeConfig = AVAILABLE_SCOPES.find(s => s.id === scopeId);
        return scopeConfig?.scopes || [];
      }),
    ];

    const state = btoa(JSON.stringify({ tenantId: activeTenantId }));
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.set('client_id', GOOGLE_CLIENT_ID);
    authUrl.searchParams.set('redirect_uri', GOOGLE_REDIRECT_URI);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', scopes.join(' '));
    authUrl.searchParams.set('access_type', 'offline');
    authUrl.searchParams.set('prompt', 'consent');
    authUrl.searchParams.set('state', state);

    window.location.href = authUrl.toString();
  };

  // Toggle scope selection
  const toggleScope = (scopeId: string) => {
    setSelectedScopes(prev =>
      prev.includes(scopeId)
        ? prev.filter(s => s !== scopeId)
        : [...prev, scopeId]
    );
  };

  if (isLoadingIntegration) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
              <svg className="h-6 w-6" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            </div>
            <div>
              <CardTitle>Google</CardTitle>
              <CardDescription>
                Connect Google Calendar and Sheets
              </CardDescription>
            </div>
          </div>
          {integration?.status === 'active' && (
            <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
              <Check className="h-3 w-3 mr-1" />
              Connected
            </Badge>
          )}
          {integration?.status === 'error' && (
            <Badge variant="destructive">
              <AlertCircle className="h-3 w-3 mr-1" />
              Error
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Error Alert */}
        {integration?.status === 'error' && integration.last_error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {integration.last_error}. Please reconnect your Google account.
            </AlertDescription>
          </Alert>
        )}

        {/* Not Connected State */}
        {!integration && (
          <div className="space-y-6">
            <p className="text-muted-foreground">
              Connect your Google account to sync calendar events and export call data to spreadsheets.
            </p>

            {/* Scope Selection */}
            <div className="space-y-4">
              <Label>Select services to connect:</Label>
              {AVAILABLE_SCOPES.map(scope => {
                const Icon = scope.icon;
                return (
                  <div
                    key={scope.id}
                    className="flex items-center space-x-4 p-4 border rounded-lg cursor-pointer hover:bg-muted/50"
                    onClick={() => toggleScope(scope.id)}
                  >
                    <Checkbox
                      checked={selectedScopes.includes(scope.id)}
                      onCheckedChange={() => toggleScope(scope.id)}
                    />
                    <div className={`p-2 rounded-lg ${scope.iconBgClass}`}>
                      <Icon className={`h-5 w-5 ${scope.iconColorClass}`} />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{scope.name}</p>
                      <p className="text-sm text-muted-foreground">{scope.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <Button
              onClick={handleConnect}
              className="gap-2"
              disabled={selectedScopes.length === 0}
            >
              <ExternalLink className="h-4 w-4" />
              Connect Google
            </Button>
          </div>
        )}

        {/* Connected State */}
        {integration?.status === 'active' && (
          <>
            {/* Account Info */}
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarImage src={integration.config.picture} />
                    <AvatarFallback>
                      <Mail className="h-4 w-4" />
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{integration.config.name || 'Google Account'}</p>
                    <p className="text-sm text-muted-foreground">{integration.config.email}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => refreshTokenMutation.mutate()}
                  disabled={refreshTokenMutation.isPending}
                >
                  <RefreshCw className={`h-4 w-4 ${refreshTokenMutation.isPending ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>

            {/* Connected Services */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Settings2 className="h-4 w-4" />
                <h4 className="font-medium">Connected Services</h4>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className={`flex items-center gap-3 p-3 border rounded-lg ${integration.config.calendar_enabled ? '' : 'opacity-50'}`}>
                  <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <Calendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Google Calendar</p>
                    <p className="text-xs text-muted-foreground">
                      {integration.config.calendar_enabled ? 'Connected' : 'Not connected'}
                    </p>
                  </div>
                </div>

                <div className={`flex items-center gap-3 p-3 border rounded-lg ${integration.config.sheets_enabled ? '' : 'opacity-50'}`}>
                  <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                    <FileSpreadsheet className="h-4 w-4 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Google Sheets</p>
                    <p className="text-xs text-muted-foreground">
                      {integration.config.sheets_enabled ? 'Connected' : 'Not connected'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Calendar Settings */}
            {integration.config.calendar_enabled && (
              <div className="space-y-4">
                <h4 className="font-medium">Calendar Settings</h4>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-0.5">
                    <Label>Auto-create Callback Events</Label>
                    <p className="text-sm text-muted-foreground">
                      Automatically create calendar events for scheduled callbacks
                    </p>
                  </div>
                  <Switch
                    checked={autoCreateEvents}
                    onCheckedChange={setAutoCreateEvents}
                  />
                </div>
              </div>
            )}

            {/* Last Sync Info */}
            {integration.last_sync_at && (
              <p className="text-xs text-muted-foreground">
                Last synced: {new Date(integration.last_sync_at).toLocaleString()}
              </p>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 pt-4 border-t">
              <Button
                onClick={() => saveSettingsMutation.mutate()}
                disabled={saveSettingsMutation.isPending}
              >
                {saveSettingsMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Save Settings
              </Button>

              <Button
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => disconnectMutation.mutate()}
                disabled={disconnectMutation.isPending}
              >
                {disconnectMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Disconnect
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
